/**
 * Optional contact (intake/spec §3.6, FR-6, NFR-3). Collapsed by default and clearly optional:
 * "Want updates? (optional)". Expands to name/email/phone (all optional). Anonymous is welcome and
 * treated the same — the persistent note says so. Reporter PII is never shown publicly (NFR-3).
 */
import { useId, useState } from "react";
import { Field } from "./Field.js";

export interface ContactValue {
  name: string;
  email: string;
  phone: string;
}

interface Props {
  value: ContactValue;
  onChange: (v: ContactValue) => void;
  emailError?: string;
}

export function ContactSection({ value, onChange, emailError }: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const nameId = useId();
  const emailId = useId();
  const phoneId = useId();

  const set = (patch: Partial<ContactValue>) => onChange({ ...value, ...patch });

  return (
    <section className="td-contact">
      <button
        type="button"
        className="td-contact__toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="td-contact__toggle-title">Want updates? (optional)</span>
        <span className="td-contact__toggle-icon" aria-hidden="true">
          {open ? "–" : "+"}
        </span>
      </button>

      <div id={panelId} className="td-contact__panel" hidden={!open}>
        <p className="td-contact__reassure">
          Leave blank to report anonymously. We only use this to tell you what happens with this
          concern.
        </p>

        <Field id={nameId} label="Name" optionalHint>
          {(aria) => (
            <input
              {...aria}
              className="td-input"
              type="text"
              autoComplete="name"
              value={value.name}
              onChange={(e) => set({ name: e.target.value })}
            />
          )}
        </Field>

        <Field id={emailId} label="Email" optionalHint error={emailError}>
          {(aria) => (
            <input
              {...aria}
              className="td-input"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={value.email}
              onChange={(e) => set({ email: e.target.value })}
            />
          )}
        </Field>

        <Field id={phoneId} label="Phone" optionalHint>
          {(aria) => (
            <input
              {...aria}
              className="td-input"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={value.phone}
              onChange={(e) => set({ phone: e.target.value })}
            />
          )}
        </Field>

        <p className="td-contact__anon-note" role="note">
          Anonymous reports are welcome and treated the same.
        </p>
      </div>
    </section>
  );
}
